import type { OverviewDto } from '@octopus-do/octopus-api-client'
import { parseNestError } from '@store/reducers/dashboard/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

import { NestClient } from '@/nest'

// A crawl the API started answers on its own schedule — there is no progress to
// show, only the moment it lands — so while one is running the overview is
// asked again every few seconds and the screen fills in by itself.
const CRAWL_POLL_MS = 5_000

export const useReportOverview = (uuid?: string) => {
  const [overview, setOverview] = useState<OverviewDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Only the first answer is worth a spinner. A poll that quietly replaces the
  // overview must not blank the screen every few seconds.
  const loaded = useRef(false)

  const load = useCallback(() => {
    if (!uuid) {
      return
    }

    if (!loaded.current) {
      setLoading(true)
    }

    NestClient.sitemapReport
      .overview(uuid)
      .then((data) => {
        setOverview(data)
        setError(null)
      })
      .catch((cause) => setError(parseNestError(cause)))
      .finally(() => {
        loaded.current = true
        setLoading(false)
      })
  }, [uuid])

  useEffect(load, [load])

  useEffect(() => {
    if (!overview?.crawling) {
      return
    }

    const timer = setInterval(load, CRAWL_POLL_MS)

    return () => clearInterval(timer)
  }, [load, overview?.crawling])

  return { error, loading, overview, reload: load }
}
